import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield } from "lucide-react";
import { submitLead } from "@/lib/submitLead";

const PHONE_REGEX = /^(?:(?:\+33|0033|0)\s?[67])(?:[\s.-]?\d{2}){4}$/;
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const CtaSection = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ prenom: "", email: "", telephone: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.prenom.trim()) errs.prenom = "Prénom requis";
    if (!form.email.trim()) errs.email = "Email requis";
    else if (!EMAIL_REGEX.test(form.email.trim())) errs.email = "Email invalide";
    if (!form.telephone.trim()) errs.telephone = "Téléphone requis";
    else if (!PHONE_REGEX.test(form.telephone.trim().replace(/\s/g, ""))) errs.telephone = "Numéro français invalide (06, 07 ou +33)";
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setLoading(true);
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    try {
      await submitLead({
        prenom: form.prenom.trim(),
        email: form.email.trim(),
        telephone: form.telephone.trim(),
        identiteNumerique: "",
        source: "google-ads",
        code,
      });
    } catch (err) {
      console.error("Submit error", err);
    }
    setLoading(false);
    navigate("/merci", { state: { code } });
  };

  const inputClass = (field: string) =>
    `w-full rounded-lg border px-4 py-3 text-white placeholder:text-white/70 outline-none backdrop-blur-sm focus:ring-2 focus:ring-white/40 sm:flex-1 ${
      errors[field] ? "border-red-400 bg-red-500/20" : "border-white/30 bg-white/20"
    }`;

  return (
    <section className="section-padding trust-band">
      <div className="container mx-auto text-center text-primary-foreground">
        <h2 className="mb-4 text-3xl font-extrabold md:text-4xl">
          Prêt(e) à Reprendre les Rênes de Votre Carrière ?
        </h2>
        <p className="mx-auto mb-10 max-w-xl text-primary-foreground/80">
          Vérifiez votre éligibilité CPF en 2 minutes — 100% financé par votre CPF
        </p>

        <form
          onSubmit={handleSubmit}
          className="mx-auto flex max-w-2xl flex-col items-center gap-3"
        >
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:gap-2">
            <div className="flex flex-col gap-1 sm:flex-1">
              <input type="text" name="prenom" placeholder="Prénom" required value={form.prenom} onChange={handleChange} className={inputClass("prenom")} />
              {errors.prenom && <p className="text-xs text-red-200">{errors.prenom}</p>}
            </div>
            <div className="flex flex-col gap-1 sm:flex-1">
              <input type="email" name="email" placeholder="Email" required value={form.email} onChange={handleChange} className={inputClass("email")} />
              {errors.email && <p className="text-xs text-red-200">{errors.email}</p>}
            </div>
            <div className="flex flex-col gap-1 sm:flex-1">
              <input type="tel" name="telephone" placeholder="Téléphone (06 12 34 56 78)" required value={form.telephone} onChange={handleChange} className={inputClass("telephone")} />
              {errors.telephone && <p className="text-xs text-red-200">{errors.telephone}</p>}
            </div>
          </div>
          <button type="submit" disabled={loading} className="btn-cta w-full whitespace-nowrap sm:w-auto">
            {loading ? "Envoi..." : "Vérifier mon éligibilité gratuitement"}
          </button>
          <p className="text-xs text-white/90">
            Réponse sous 24h • 100% confidentiel • Sans engagement
          </p>
        </form>

        <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-primary-foreground/70">
          <Shield className="h-3.5 w-3.5" /> Sans avance de frais — 100% financé par votre CPF
        </p>
      </div>
    </section>
  );
};

export default CtaSection;
